import type { GetAccountUsageQuery } from '@frameio/graphql-client/generated';
import { useAccountPermissions } from '@frameio/permissions';
import { getLink } from '@frameio/shared/routing/getLink';
import { AlertBanner } from '@frameio/vapor';
import { BannerWrapper } from './BannerWrapper';
import TEXT from './generated/TEXT/PaymentAlertBanner';
import { UniversalLink } from './UniversalLink';

const SETTINGS_BILLING = getLink({ page: 'settings', section: 'billing' });

TEXT.DEFINE(
  {
    /** Banner title */
    title: 'Payment Problem',

    /** Message for users without billing permissions */
    detailsNoPermission: (ownerEmail: string) => (
      <>
        Contact account owner{' '}
        <UniversalLink href={`mailto:${ownerEmail}`} target="_blank">
          ({ownerEmail})
        </UniversalLink>{' '}
        for assistance or account will be locked.
      </>
    ),

    /** Message for users with billing permissions */
    detailsWithPermission: () => (
      <>
        <UniversalLink href={SETTINGS_BILLING}>
          Update payment method
        </UniversalLink>{' '}
        to try again or account will be locked.
      </>
    ),
  },
  { UniversalLink, SETTINGS_BILLING }
);

type PaymentAlertBannerProps = {
  account: NonNullable<GetAccountUsageQuery['account']>;
};

export function PaymentAlertBanner({ account }: PaymentAlertBannerProps) {
  const accountId = account.id;
  const canManageBilling =
    useAccountPermissions({
      accountId,
      check: 'canManageBilling',
    }) ?? false;

  const balance = account.subscription?.balance ?? 0;
  const isAccountLocked = !!account.lockedAt;
  const showBanner = balance > 0 && !isAccountLocked;
  const ownerEmail = account.owner?.email ?? '';

  if (!showBanner) return null;

  const details = canManageBilling
    ? TEXT.detailsWithPermission()
    : TEXT.detailsNoPermission(ownerEmail);

  return (
    <BannerWrapper>
      <AlertBanner details={details} title={TEXT.title} variant="notice" />
    </BannerWrapper>
  );
}
